import { Button, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { ServiceAgreementsView } from './ServiceAgreementsView.js';
import { useAgreementsQuery } from './useAgreementsQuery.js';

/**
 * The `/service-agreements` route component (promoted from a ComingSoon stub this plan). Reads the
 * typed `status` search param, fires the READ-ONLY coarse-endpoint query, and renders the faithful
 * `ServiceAgreementsView` on success. Loading → a page-shaped `RouteSkeleton`; error → announced error state
 * with a retry that refetches (the estimates/invoices 3-state contract, verbatim tokens).
 *
 * The server computes the enriched list + status counts + summary; this component never re-aggregates
 * and never sends a tenant id (SEC-11). Rendering the list NEVER generates an invoice (D-03).
 */

const routeApi = getRouteApi('/service-agreements');

export function ServiceAgreementsRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useAgreementsQuery(search);

  if (query.isPending) {
    return (
      <div className="min-h-full bg-background px-6 py-8">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-semibold text-3xl text-foreground">
              {t('serviceAgreements:title')}
            </h1>
            <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
              {t('serviceAgreements:subtitle')}
            </p>
          </div>
          <Button asChild>
            <Link to="/service-agreements/new">{t('serviceAgreements:actions.newAgreement')}</Link>
          </Button>
        </header>
        <RouteSkeleton label={t('serviceAgreements:title')} rows={6} columns={4} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('serviceAgreements:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">
          {t('serviceAgreements:error.body')}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('serviceAgreements:error.retry')}
        </button>
      </section>
    );
  }

  return <ServiceAgreementsView data={query.data} search={search} />;
}
